import {padNum, performNextTick, texture} from "p3";
import Enemy from "./Enemy";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";

export default class EnemySnapperPlant extends Enemy {
    constructor() {
        super(definitions.ENEMY_ID.SNAPPER_PLANT);

        this.health = 2;

        this.moveSpeed = 0;

        this.collisionDynamic = false;
        this.collisionRadius = 10;
        this.collisionOffsetX = 4;
        this.collisionOffsetY = -40;

        this._defaultAnimationSpeed = 15;
        this._chaseAnimationSpeed = 8;

        this._attackRange = 140;
        this._attackTime = 0;
        this._attackDelay = 0.5;
        this._isAttacking = false;

        this._isTakingDamage = false;

        this._defaultTween = null;

        this._initialShadowScale = 0;
    }

    init() {
        super.init();

        let frames = [];
        for (let i = 0; i < 1; ++i) {
            frames[i] = texture("enemy_prickly_snapperplant_idle_" + padNum(i, 2));
        }
        const sequence = new P3.MovieClipSequence(frames, "idle");

        frames = [];
        for (let i = 0; i < 2; ++i) {
            frames[i] = texture("enemy_prickly_snapperplant_move_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "move");

        frames = [];
        for (let i = 0; i < 9; ++i) {
            frames[i] = texture("enemy_prickly_snapperplant_attack_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "attack");

        frames = [];
        for (let i = 0; i < 7; ++i) {
            frames[i] = texture("enemy_prickly_snapperplant_hit_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "take_damage");

        frames = [];
        for (let i = 0; i < 7; ++i) {
            frames[i] = texture("enemy_prickly_snapperplant_defeat_" + padNum(i, 2));
        }
        sequence.addTextures(frames, "dead");

        this.addViewWithMovieClip(sequence, "idle");

        this.state = definitions.ENEMY_STATE.WANDER;

        this._attackTime = this._attackDelay;
    }

    onViewAdded() {
        super.onViewAdded();

        this._view.anchor.set(0.5, 1);
        this._view.animationSpeed = this._defaultAnimationSpeed;
        this._view.gotoAndPlay("idle", true);

        this._view.addFrameScript(2, () => this.onAttackDamage(), null, "attack");
        this._view.addFrameScript(6, () => this.onAttackFinished(), null, "attack");
        this._view.addFrameScript(6, () => this.onTakeDamageFinished(), null, "take_damage");

        this._initialShadowScale = 0.7;
        this.addShadow(definitions.SHADOW_SIZE.MEDIUM, this._initialShadowScale, new PIXI.Point(5, -23));
    }

    /**
     * @private
     */
    _startAttack() {
        if (!this._isAttacking) {
            this._attackTime = 0;
            this._isAttacking = true;
            this._view.gotoAndPlay("attack", false);

            P3.audio.playSound([
                "sfx_enemy_snapper_plant_bite_00",
                "sfx_enemy_snapper_plant_bite_01",
                "sfx_enemy_snapper_plant_bite_02"
            ]);

            // face target
            const x = this.target.x - this.x;
            this.scale.x = Math.sign(x);
        }
    }

    /**
     * @private
     */
    _startTakeDamage() {
        this._isTakingDamage = true;
        this._view.animationSpeed = this._defaultAnimationSpeed;
        this._view.gotoAndPlay("take_damage", false);
    }

    /**
     * @private
     */
    _startDead() {
        this._view.gotoAndPlay("dead", false);
    }

    /**
     * @return {boolean}
     * @private
     */
    _isTargetInChaseRange() {
        if (this.target) {
            const to = new P3.Vector2(this.target.x - this.x, this.target.y - this.y);
            return to.lengthSq < this._chaseRange ** 2;
        }
        return false;
    }

    /**
     * @return {boolean}
     * @private
     */
    _isTargetInAttackRange() {
        if (this.target) {
            const to = new P3.Vector2(this.target.x - this.x, this.target.y - this.y);
            return to.lengthSq < this._attackRange ** 2;
        }
        return false;
    }

    /**
     * @private
     */
    _stopAnimations() {
        if (this._defaultTween) {
            GameScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    /**
     * @param {number} state
     * @private
     */
    onStateEnter(state) {
        switch (state) {
            case definitions.ENEMY_STATE.IDLE: {
                break;
            }
            case definitions.ENEMY_STATE.WANDER: {
                this._view.animationSpeed = this._defaultAnimationSpeed;
                this._view.gotoAndPlay("idle", true);
                break;
            }
            case definitions.ENEMY_STATE.CHASE: {
                this._attackTime = 0;
                this._view.animationSpeed = this._chaseAnimationSpeed;
                this._view.gotoAndPlay("move", true);
                break;
            }
            case definitions.ENEMY_STATE.ATTACK: {
                this._view.animationSpeed = this._defaultAnimationSpeed;
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                this._view.animationSpeed = this._defaultAnimationSpeed;
                this._startTakeDamage();
                break;
            }
            case definitions.ENEMY_STATE.DEAD: {
                this._view.animationSpeed = this._defaultAnimationSpeed;
                this._startDead();
                break;
            }
        }
    }

    /**
     * @param {number} state
     * @private
     */
    onStateUpdate(state) {
        switch (state) {
            case definitions.ENEMY_STATE.IDLE: {
                break;
            }
            case definitions.ENEMY_STATE.WANDER: {
                if (this._isTargetInAttackRange()) {
                    this.state = definitions.ENEMY_STATE.CHASE;
                }
                break;
            }
            case definitions.ENEMY_STATE.CHASE: {
                if (!this._isTargetInAttackRange()) {
                    this.state = definitions.ENEMY_STATE.WANDER;
                }

                if (this.isAttackReady) {
                    this.state = definitions.ENEMY_STATE.ATTACK;
                }
                this._attackTime += window.dt;
                break;
            }
            case definitions.ENEMY_STATE.ATTACK: {
                if (!this.isAttacking && this.isAttackReady) {
                    this._startAttack();
                }
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                break;
            }
            case definitions.ENEMY_STATE.DEAD: {
                break;
            }
        }
    }

    /**
     * @param {number} state
     * @private
     */
    onStateExit(state) {
        switch (state) {
            case definitions.ENEMY_STATE.ATTACK: {
                this._isAttacking = false;
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                this._isTakingDamage = false;
                break;
            }
        }
    }

    onAttackDamage() {
        this._isTargetInAttackRange() && this.signals.attack.dispatch(this, this.target, this.damage);
    }

    onAttackFinished() {
        this._attackTime = 0;
        this._isAttacking = false;

        this.state = definitions.ENEMY_STATE.CHASE;
    }

    onTakeDamageFinished() {
        performNextTick(() => {
            this.state = definitions.ENEMY_STATE.CHASE;
        });
    }

    onDeadFinished() {
        this._view.visible = false;
    }

    /**
     * @param {number} damage
     */
    onTakeDamage(damage) {
    }

    onDead() {
        this._stopAnimations();

        this._defaultTween = gsap.timeline();
        GameScene.active.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._view.scale, 0.24, {
            x: 0,
            y: 0,
            ease: Back.easeIn,
            onComplete: () => {
                this._view.visible = false;
            }
        }), 0);
        this._defaultTween.add(gsap.to(this._shadow.scale, 0.24, {
            x: 0,
            y: 0,
            ease: Back.easeIn,
            onComplete: () => {
                this._shadow.visible = false;
            }
        }), 0);

        super.onDead();
    }

    onRespawn() {
        this._stopAnimations();

        this._defaultTween = gsap.timeline();
        GameScene.active.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._view.scale, 0.24, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
            onStart: () => {
                this._view.visible = true;
            }
        }), 0);
        this._defaultTween.add(gsap.to(this._shadow.scale, 0.24, {
            x: this._initialShadowScale,
            y: this._initialShadowScale,
            ease: Back.easeOut,
            onStart: () => {
                this._shadow.visible = true;
            }
        }), 0);

        super.onRespawn();
    }

    /**
     * @return {boolean}
     */
    get isAttacking() {
        return this._isAttacking;
    }

    /**
     * @return {boolean}
     */
    get isTakingDamage() {
        return this._isTakingDamage;
    }

    /**
     * @return {boolean}
     */
    get isAttackReady() {
        return this._attackTime >= this._attackDelay;
    }
}
